/**
 * Dashboard stats cards: Total Lambdas, EOL Lambda Runtimes, EOL Brazil Interpreters, Job Executions.
 * Each card is clickable to filter the main view.
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import React from 'react';
import { Container, Header, SpaceBetween } from '@amzn/awsui-components-core';
import { ResourceTypeConfig } from '../config';
import { BrazilEOLInterpreterData, JobRun } from '../types';
import { FilterType } from '../hooks/useResourceFiltering';
import styles from '../styles.module.scss';

interface DashboardStatsContainerProps {
  currentConfig: ResourceTypeConfig;
  isLoading: boolean;
  statsData: {
    totalLambdas: number;
    eolCount: number;
    totalPipelines: number;
  };
  isBrazilEOLLoading: boolean;
  brazilEOLData: BrazilEOLInterpreterData | null;
  resourceTypeJobs: JobRun[];
  resourceInProgressJobs: JobRun[];
  activeFilter: FilterType;
  setActiveFilter: (filter: FilterType) => void;
  setIsViewTransitioning: (transitioning: boolean) => void;
}

export const DashboardStatsContainer: React.FC<DashboardStatsContainerProps> = ({
  currentConfig,
  isLoading,
  statsData,
  isBrazilEOLLoading,
  brazilEOLData,
  resourceTypeJobs,
  resourceInProgressJobs,
  activeFilter,
  setActiveFilter,
  setIsViewTransitioning
}) => {
  const handleFilterClick = (filter: FilterType) => {
    if (activeFilter !== filter) {
      setIsViewTransitioning(true);
      setActiveFilter(filter);
      setTimeout(() => setIsViewTransitioning(false), 100);
    }
  };

  return (
    <Container
      header={
        <Header variant="h2" description={currentConfig.description}>
          {currentConfig.icon} {currentConfig.displayName} Dashboard
        </Header>
      }
    >
      <SpaceBetween size="l">
        <div className={styles.statsGrid}>
          {/* Total Lambdas */}
          <div
            className={`${styles.statCard} ${activeFilter === null ? styles.statCardActive : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleFilterClick(null)}
          >
            <div className={styles.statNumber}>{isLoading ? '-' : statsData.totalLambdas}</div>
            <div className={styles.statLabel}>Total Lambdas</div>
          </div>

          {/* EOL Lambda Runtimes */}
          <div className={`${styles.statCard} ${activeFilter === 'eol' ? styles.statCardActive : ''}`} onClick={() => handleFilterClick('eol')}>
            <div className={styles.statNumber}>{isLoading ? '-' : statsData.eolCount}</div>
            <div className={styles.statLabel}>EOL Lambda Runtimes</div>
          </div>

          {/* EOL Brazil Interpreters */}
          <div
            className={`${styles.statCard} ${activeFilter === 'brazil-eol' ? styles.statCardActive : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleFilterClick('brazil-eol')}
          >
            <div className={styles.statNumber}>
              {isBrazilEOLLoading ? '-' : (brazilEOLData?.cpythonEOLCount || 0) + (brazilEOLData?.nodejsEOLCount || 0)}
            </div>
            <div className={styles.statLabel}>EOL Brazil Interpreters</div>
          </div>

          {/* Job Executions */}
          <div
            className={`${styles.statCard} ${activeFilter === 'job-executions' ? styles.statCardActive : ''} ${
              resourceInProgressJobs.length > 0 ? styles.statCardHighlighted : ''
            }`}
            onClick={() => handleFilterClick('job-executions')}
          >
            <div className={styles.statNumber}>{resourceTypeJobs.length}</div>
            <div className={styles.statLabel}>Job Executions</div>
          </div>
        </div>
      </SpaceBetween>
    </Container>
  );
};
